import { useState, useEffect, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ContributeModal from "@/components/ContributeModal";
import ShareModal from "@/components/ShareModal";
import ClaimModal from "@/components/ClaimModal";
import EditProfileModal from "@/components/EditProfileModal";
import { getEntity, getEntityContributions, getEntitySupporters, getLeaderboard, getEntityFlips, authWallet, invalidatePublicData } from "@/lib/api";
import { setShareMeta, backendOrigin } from "@/lib/meta";
import { signEvmMessage } from "@/lib/onchain";
import { useWallet } from "@/context/WalletContext";
import {
  fmtUSD, fmtUSDprecise, fmtNum, fmtToken, timeAgo, entityLabel, entityName, entityImage,
  CHAIN_LABELS, explorerTx, explorerAddress, shortAddress,
} from "@/lib/format";
import { Crown, ArrowUp, ArrowDown, Minus, ExternalLink, Globe, Twitter, Send, ShieldCheck, BadgeCheck, Pencil, Loader2 } from "lucide-react";
import { CoinIcon } from "@/components/CoinIcon";
import ClassificationBadges from "@/components/ClassificationBadges";
import { toast } from "sonner";

export default function EntityDetail({ category }) {
  const { slug } = useParams();
  const { wallet } = useWallet();
  const [entity, setEntity] = useState(null);
  const [contribs, setContribs] = useState([]);
  const [supporters, setSupporters] = useState([]);
  const [board, setBoard] = useState([]);
  const [showBack, setShowBack] = useState(false);
  const [shareData, setShareData] = useState(null);
  const [showClaim, setShowClaim] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editToken, setEditToken] = useState(null);
  const [flips, setFlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [proving, setProving] = useState(false);

  const load = useCallback(async () => {
    const entityPromise = getEntity(slug);
    const contributionsPromise = getEntityContributions(slug);
    const supportersPromise = getEntitySupporters(slug);
    const flipsPromise = getEntityFlips(slug);
    const boardPromise = getLeaderboard(category);
    try {
      const e = await entityPromise;
      setEntity(e);
      setEditToken(localStorage.getItem(`flip_edit_${e.id}`) || null);
      setLoading(false);

      const [cResult, sResult, flipsResult, boardResult] = await Promise.allSettled([
        contributionsPromise, supportersPromise, flipsPromise, boardPromise,
      ]);
      if (cResult.status === "fulfilled") setContribs(cResult.value);
      if (sResult.status === "fulfilled") setSupporters(sResult.value);
      if (flipsResult.status === "fulfilled") setFlips(flipsResult.value);
      if (boardResult.status === "fulfilled") setBoard(boardResult.value.entities);
    } catch (e) {
      setNotFound(true);
      setLoading(false);
    }
  }, [category, slug]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // Dynamic share meta: title + OG card served by the backend
  useEffect(() => {
    if (!entity) return;
    const isCaller = (entity.category || category) === "callers";
    const label = isCaller
      ? (entity.display_name || (entity.handle ? `@${entity.handle}` : null) || entity.slug || "FLIP")
      : (entity.ticker || entity.name || "FLIP");
    const rank = entity.leaderboard_rank || entity.rank;
    const backedNum = Number(entity.total_backed_usd || 0);
    const backed = backedNum.toLocaleString("en-US", { maximumFractionDigits: 0 });
    const pathPrefix = isCaller ? "caller" : "token";
    const shareNonce = (new URLSearchParams(window.location.search).get("share") || "")
      .replace(/[^A-Za-z0-9_-]/g, "")
      .slice(0, 100);
    // Tokens keep a stable ?v=4 URL. Callers bust crawler caches when
    // rank / backing / updated_at change so X previews refresh with the board.
    const stampSource = entity.updated_at
      || entity.last_contribution?.created_at
      || entity.created_at
      || "";
    const stamp = String(stampSource).replace(/[^A-Za-z0-9]/g, "").slice(0, 20) || "0";
    const versionQuery = isCaller
      ? `v=4&r=${rank ?? 0}&b=${Math.round(backedNum)}&t=${stamp}`
      : "v=4";
    const shareSuffix = shareNonce ? `&share=${shareNonce}` : "";
    setShareMeta({
      title: entity.slug === "duko"
        ? `${label} Crypto Ranking · #${rank ?? "?"} on FLIP — $${backed} Backed On-Chain`
        : `${label} · #${rank ?? "?"} on FLIP — $${backed} backed`,
      description: `Back ${label} on FLIP — the people-powered crypto leaderboard. Every dollar verified on-chain.`,
      image: `${backendOrigin()}/api/og/v4/${entity.slug}.png?${versionQuery}${shareSuffix}`,
      url: `https://backonflip.com/${pathPrefix}/${entity.slug}${shareNonce ? `?share=${shareNonce}` : ""}`,
      type: "profile",
      robots: entity.status === "active" && entity.listed ? "index,follow" : "noindex,follow",
    });
  }, [entity, category]);

  const onSuccess = (result) => {
    setShowBack(false);
    setShareData(result);
    invalidatePublicData(["leaderboard"]);
    load();
    result.events.forEach((ev) => toast.success(ev.title, { duration: 6000 }));
  };

  // Cross-device ownership: sign with the wallet linked at claim time and get
  // this browser's edit token — no login, works from any device.
  const proveOwnership = async () => {
    setProving(true);
    try {
      const msg = `FLIP profile access\nentity: ${entity.id}\n\nSigning proves you own this profile on FLIP.`;
      const { address, signature } = await signEvmMessage(msg);
      const r = await authWallet(entity.id, address, signature);
      localStorage.setItem(`flip_edit_${entity.id}`, r.edit_token);
      setEditToken(r.edit_token);
      setShowEdit(true);
      toast.success("Ownership verified — editor unlocked");
    } catch (e) {
      if (e?.code === 4001) toast.error("Signature rejected in wallet");
      else toast.error(e.response?.data?.detail || e.message || "Could not verify ownership");
    } finally { setProving(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen">
        <Navbar category={category} />
        <div className="mx-auto max-w-5xl px-4 py-24 text-center" data-testid="entity-not-found">
          <div className="font-display text-3xl font-black uppercase tracking-tight">Nothing here</div>
          <p className="mt-2 text-sm text-zinc-500">This token or profile doesn't exist (yet).</p>
          <Link to={category === "callers" ? "/callers" : "/"} data-testid="not-found-back"
            className="mt-6 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-bold text-black">
            Back to leaderboard
          </Link>
        </div>
      </div>
    );
  }

  if (loading || !entity) {
    const loadingLabel = (slug || "FLIP").replace(/[-_]+/g, " ").toUpperCase();
    const entryType = category === "callers" ? "caller" : "token";
    return (
      <div className="min-h-screen">
        <Navbar category={category} />
        <main className="mx-auto max-w-5xl px-4 py-16">
          <section className="rounded-2xl border border-white/10 bg-[#080808] p-6">
            <h1 className="font-display text-3xl font-black tracking-tight">{loadingLabel} {entryType} ranking on FLIP</h1>
            <div className="mt-3 max-w-3xl space-y-3 text-sm leading-6 text-zinc-400">
              <p>FLIP ranks this {entryType} by confirmed supporter contributions, not social votes, follower counts, trading volume or hidden paid placement.</p>
              <p>Each accepted contribution adds its verified dollar value after confirmation. When the total passes the entry above it, the live leaderboard changes position.</p>
              <p>The public profile shows verified backing, supporter activity, contribution history and recent rank changes. The ranking records support contributed through FLIP; it is not a price forecast, investment recommendation or promise of returns.</p>
            </div>
          </section>
        </main>
      </div>
    );
  }

  const isToken = entity.category === "tokens";
  const img = entityImage(entity);
  const rank = entity.leaderboard_rank || entity.rank;
  const prev = entity.previous_rank;
  const change = prev == null || prev === rank ? "same" : rank < prev ? "up" : "down";

  return (
    <div className="min-h-screen grain">
      <Navbar category={category} />
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Link to={isToken ? "/" : "/callers"} className="text-xs text-zinc-500 hover:text-white" data-testid="back-to-board">← Back to leaderboard</Link>

        {/* Header */}
        <div className="mt-4 flex flex-col gap-6 rounded-2xl border border-white/10 bg-[#080808] p-6 sm:flex-row sm:items-center" data-testid="entity-header">
          <div className="flex items-center gap-4">
            {img ? (
              <img src={img} alt={entityLabel(entity)} className="h-20 w-20 rounded-2xl border border-white/10 object-cover" />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br from-zinc-800 to-zinc-900 font-display text-2xl font-black">
                {entityLabel(entity).replace("@", "").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-3xl font-black tracking-tight">{entityLabel(entity)}</h1>
                {!isToken && entity.verified && (
                  <span className="flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400" data-testid="verified-badge">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified
                  </span>
                )}
                {!entity.claimed && (isToken ? !!entity.twitter : !!entity.handle) && (
                  <button onClick={() => setShowClaim(true)} data-testid="claim-profile-button"
                    className="rounded-full border border-sky-400/40 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400 transition-colors hover:bg-sky-400/10">
                    {isToken ? "Claim profile" : "Claim this profile"}
                  </button>
                )}
                {entity.claimed && (
                  <span className="flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-sky-400" data-testid="claimed-badge">
                    <BadgeCheck className="h-3.5 w-3.5" /> {isToken ? "Official" : "Verified"}
                  </span>
                )}
                {entity.demo && (
                  <span className="rounded border border-gold/30 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-gold/80">Demo</span>
                )}
                {entity.claimed && !editToken && (
                  <button onClick={proveOwnership} data-testid="prove-ownership-button" disabled={proving}
                    className="flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-zinc-400 hover:text-white disabled:opacity-40">
                    {proving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Pencil className="h-3 w-3" />} Prove ownership
                  </button>
                )}
              </div>
              <div className="truncate text-zinc-500">{entityName(entity)}</div>
              {!isToken && entity.bio && <p className="mt-1 max-w-md text-sm text-zinc-400">{entity.bio}</p>}
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-zinc-500">
                {entity.twitter && <a href={entity.twitter} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Twitter className="h-3.5 w-3.5" /> X</a>}
                {entity.website && <a href={entity.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Globe className="h-3.5 w-3.5" /> Website</a>}
                {entity.telegram && <a href={entity.telegram} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Send className="h-3.5 w-3.5" /> Telegram</a>}
                {isToken && <span>{CHAIN_LABELS[entity.chain] || entity.chain}</span>}
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col items-start gap-4 sm:items-end">
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1 font-mono text-3xl font-black ${rank === 1 ? "text-gold" : "text-white"}`}>
                {rank === 1 && <Crown className="h-6 w-6 fill-gold text-gold" strokeWidth={1.5} />}#{rank}
              </div>
              {change !== "same" && (
                <span className={`flex items-center text-xs font-bold ${change === "up" ? "text-positive" : "text-negative"}`}>
                  {change === "up" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}{Math.abs(prev - rank)}
                </span>
              )}
            </div>
            <button
              data-testid="entity-back-cta"
              onClick={() => setShowBack(true)}
              className="rounded-full bg-white px-8 py-3 font-display text-sm font-black uppercase tracking-wide text-black transition-transform hover:bg-zinc-200 active:scale-95"
            >
              Back {entityLabel(entity)}
            </button>
            {entity.claimed && (
              editToken ||
              (wallet?.address && entity.owner_wallet &&
                wallet.address.toLowerCase() === String(entity.owner_wallet).toLowerCase())
            ) && (
              <Link
                to={`/battles/create?challenger=${entity.slug}`}
                data-testid="entity-start-battle"
                className="rounded-full border border-gold/40 px-6 py-3 font-display text-sm font-black uppercase tracking-wide text-gold transition-colors hover:bg-gold/10"
              >
                Start a Battle
              </Link>
            )}
            {entity.claimed && (
              editToken ||
              (wallet?.address && entity.owner_wallet &&
                wallet.address.toLowerCase() === String(entity.owner_wallet).toLowerCase())
            ) && (
              <Link
                to={`/project/${entity.slug}/war-room`}
                data-testid="entity-war-room"
                className="rounded-full border border-white/15 px-6 py-3 font-display text-sm font-black uppercase tracking-wide text-zinc-300 transition-colors hover:border-white/30 hover:text-white"
              >
                War Room
              </Link>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Total backed" value={fmtUSD(entity.total_backed_usd)} accent />
          <Stat label="Supporters" value={fmtNum(entity.contributors)} />
          <Stat label="24h" value={`+${fmtUSD(entity.amount_24h)}`} positive />
          <Stat
            label={rank === 1 ? "Lead" : `Gap to #${rank - 1}`}
            value={
              rank === 1
                ? (entity.lead_over_next != null ? fmtUSD(entity.lead_over_next) : "👑 #1")
                : (entity.gap_to_overtake != null || entity.gap_to_next != null
                  ? fmtUSD(entity.gap_to_overtake ?? entity.gap_to_next)
                  : "—")
            }
            gold
          />
        </div>

        {entity.gap_to_next != null && (
          <div className="mt-3 rounded-xl border border-gold/20 bg-gold/[0.04] px-4 py-3 text-center font-display text-lg font-black tracking-tight text-gold" data-testid="gap-banner">
            {fmtUSD(entity.gap_to_next)} TO TAKE {rank - 1 === 0 ? "THE CROWN 👑" : `#${rank - 1}`}
          </div>
        )}

        {isToken && (
          <div className="mt-4 space-y-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3 text-xs text-zinc-400" data-testid="contract-display">
            <div className="flex items-start gap-2">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-zinc-300">Contract</span>
                  <span className="rounded border border-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-300/90">
                    {CHAIN_LABELS[entity.chain] || entity.chain}
                    {entity.chain_id ? ` · ${entity.chain_id}` : entity.chain === "robinhood" ? " · 4663" : ""}
                  </span>
                  <ClassificationBadges entity={entity} />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 font-mono text-[11px] text-zinc-500">
                  <span data-testid="entity-contract-short">{shortAddress(entity.contract_address)}</span>
                  <button
                    type="button"
                    data-testid="entity-copy-contract"
                    className="text-zinc-400 hover:text-white"
                    onClick={() => {
                      if (entity.contract_address) navigator.clipboard.writeText(entity.contract_address);
                    }}
                  >
                    Copy
                  </button>
                  {explorerAddress(entity.chain, entity.contract_address) && (
                    <a
                      href={explorerAddress(entity.chain, entity.contract_address)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="entity-explorer-link"
                      className="hover:text-white"
                    >
                      Explorer
                    </a>
                  )}
                </div>
                <div className="mt-1 break-all font-mono text-[10px] text-zinc-600">{entity.contract_address}</div>
              </div>
            </div>
            {(entity.external_market || entity.total_backed_usd != null) && (
              <div className="grid gap-2 border-t border-white/5 pt-2 sm:grid-cols-2" data-testid="entity-backing-vs-market">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-400">BackOnFlip Rank</div>
                  <div className="font-mono text-sm text-white">
                    {fmtUSD(entity.total_backed_usd)} backed
                    {entity.rank != null ? ` · #${entity.rank}` : ""}
                  </div>
                  <div className="text-[10px] text-zinc-600">Verified Backing only</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-amber-200">Market Activity</div>
                  <div className="font-mono text-sm text-zinc-300">
                    {entity.external_market?.market_volume_24h_usd != null
                      ? `${fmtUSD(entity.external_market.market_volume_24h_usd)} 24h vol`
                      : entity.price_usd != null
                        ? `$${Number(entity.price_usd).toPrecision(4)}`
                        : "Unavailable"}
                    {entity.external_market?.stale ? " · stale" : ""}
                  </div>
                  <div className="text-[10px] text-zinc-600">External data — does not change rank</div>
                </div>
              </div>
            )}
          </div>
        )}

        <EntitySeoSummary entity={entity} rank={rank} />

        {/* Rank wars */}
        {flips.length > 0 && (
          <div className="mt-4 rounded-2xl border border-white/10 bg-[#080808]" data-testid="entity-flips">
            <div className="border-b border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">⚔️ Recent flips</div>
            <div>
              {flips.map((f) => {
                const wasFlipped = f.entity_id === entity.id;
                return (
                  <div key={f.id} className="flex items-center justify-between border-b border-white/5 px-4 py-2.5 text-sm">
                    <span>
                      {wasFlipped
                        ? <>⚠️ Flipped by <b>{f.by_label}</b> — fell to #{f.new_rank}</>
                        : <>🔥 Flipped <b>{f.entity_label}</b> with ${Number(f.usd_value).toLocaleString("en-US")}</>}
                    </span>
                    <span className="font-mono text-[11px] text-zinc-600">{timeAgo(f.created_at)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Two columns: history + supporters */}
        <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
          <div className="rounded-2xl border border-white/10 bg-[#080808]" data-testid="contribution-history">
            <div className="border-b border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">Recent contributions</div>
            <div className="max-h-[460px] overflow-y-auto">
              {contribs.map((c) => (
                <div key={c.id} className="flex items-center justify-between border-b border-white/5 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <CoinIcon
                      symbol={c.payment_token_symbol}
                      logo={isToken && c.payment_token_symbol === entity.ticker ? entity.logo : null}
                      size={26}
                    />
                    <div>
                      {c.is_house_incentive ? (
                        <span className="font-mono text-sm text-zinc-200" title="Contribution from the platform's publicly declared Incentive Fund">
                          🏦 FLIP Incentive Fund
                        </span>
                      ) : (
                        <Link to={`/user/${c.wallet_address}`} className="font-mono text-sm text-zinc-200 hover:text-white">{c.ens || c.wallet_short}</Link>
                      )}
                      <div className="font-mono text-[11px] text-zinc-600">{fmtToken(c.payment_token_amount)} {c.payment_token_symbol} · {timeAgo(c.created_at)}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-sm font-bold text-positive">{fmtUSDprecise(c.usd_value)}</div>
                    {c.simulated ? (
                      <span className="font-mono text-[10px] text-zinc-600">demo · simulated</span>
                    ) : (
                      <a href={explorerTx(c.chain, c.transaction_hash)} target="_blank" rel="noreferrer" className="flex items-center justify-end gap-1 font-mono text-[10px] text-zinc-600 hover:text-white">
                        {String(c.transaction_hash || "").slice(0, 8)}… <ExternalLink className="h-2.5 w-2.5" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
              {contribs.length === 0 && <div className="px-4 py-10 text-center text-sm text-zinc-600">No contributions yet — be the first.</div>}
            </div>
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#080808]" data-testid="top-supporters">
            <div className="border-b border-white/10 px-4 py-3 text-[11px] font-bold uppercase tracking-[0.2em]">Top backers</div>
            <div className="max-h-[460px] overflow-y-auto">
              {supporters.map((s, i) => (
                <Link to={`/user/${s.wallet_address}`} key={s.wallet_address} className="flex items-center justify-between border-b border-white/5 px-4 py-3 hover:bg-white/[0.03]">
                  <div className="flex items-center gap-3">
                    <span className={`font-mono text-sm font-bold ${i === 0 ? "text-gold" : "text-zinc-500"}`}>{i + 1}</span>
                    {s.is_house_incentive ? (
                      <span className="font-mono text-sm text-zinc-200" title="Platform's publicly declared Incentive Fund">🏦 FLIP Incentive Fund</span>
                    ) : (
                      <span className="font-mono text-sm text-zinc-200">{s.ens || s.wallet_short}</span>
                    )}
                  </div>
                  <span className="font-mono text-sm font-bold">{fmtUSD(s.total_usd)}</span>
                </Link>
              ))}
              {supporters.length === 0 && <div className="px-4 py-10 text-center text-sm text-zinc-600">No backers yet</div>}
            </div>
          </div>
        </div>
      </div>

      <ContributeModal entity={entity} entities={board} open={showBack} onClose={() => setShowBack(false)} onSuccess={onSuccess} />
      <ShareModal result={shareData} entity={shareData?.entity} open={!!shareData} onClose={() => setShareData(null)} />
      <ClaimModal
        entity={entity} open={showClaim} onClose={() => setShowClaim(false)}
        onVerified={(r) => {
          if (r.edit_token) { localStorage.setItem(`flip_edit_${entity.id}`, r.edit_token); setEditToken(r.edit_token); }
          setShowClaim(false); load();
        }}
      />
      <EditProfileModal
        entity={entity} editToken={editToken} open={showEdit} onClose={() => setShowEdit(false)}
        onSaved={(updated) => { setEntity(updated); }}
      />
    </div>
  );
}

function EntitySeoSummary({ entity, rank }) {
  const label = entityLabel(entity);
  const isToken = entity.category === "tokens";
  const backed = Number(entity.total_backed_usd || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
  const supporters = Number(entity.contributors || 0).toLocaleString("en-US");

  return (
    <section className="mt-6 rounded-2xl border border-white/10 bg-[#080808] p-6" aria-labelledby="verified-ranking-heading">
      <h2 id="verified-ranking-heading" className="font-display text-xl font-black tracking-tight">
        {label} verified ranking on FLIP
      </h2>
      <div className="mt-3 space-y-3 text-sm leading-6 text-zinc-400">
        <p>
          {label} is currently #{rank} on the FLIP {isToken ? "token" : "caller"} leaderboard with ${backed} in verified backing from {supporters} supporters. FLIP orders entries by confirmed supporter contributions, not social votes, follower counts or hidden paid placement.
        </p>
        <p>
          Each accepted contribution adds its verified dollar value after confirmation. When the total passes the entry above it, the live board changes position. The ranking records support contributed through FLIP; it is not a price forecast, investment recommendation or promise of returns.
        </p>
        <p>
          Review the public contribution history, wallets backing this entry, recent activity and rank changes below. Rankings can move whenever new backing is confirmed, so the total, supporter count and gap to the next position show the latest state of the board.
        </p>
      </div>
    </section>
  );
}

function Stat({ label, value, accent, positive, gold }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">{label}</div>
      <div className={`font-mono text-xl font-bold ${gold ? "text-gold" : positive ? "text-positive" : "text-white"}`}>{value}</div>
    </div>
  );
}
